import { ArrowDownIcon, CheckCircleIcon, CopilotLogoMd24pxIcon, DownloadBIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Badges, Button, Checkbox, FeaturedIcon, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { useState } from 'react';

import { TermsDecision, TermsResponseStatus } from '../../../data/apis/terms';
import { SapAiTermsTestIds } from '../constants';
import { useTermsScrollState } from '../hooks/useTermsScrollState';
import { SapAiTermsBody, TermsRunsText } from '../SapAiTermsBody';
import { GEN_AI_FEATURES_CONSENT, sapAiTerms } from '../sapTermsContent';
import { NoWrapText } from '../styled';
import {
  EndOfTermsMarker,
  RecordNote,
  StyledSapAiTermsDialog,
  ScrollMarker,
  TermsScrollArea,
  TermsScrollFrame
} from './styled';

type SapAiTermsDialogProps = {
  open: boolean;
  onDecision: (decision: TermsDecision, signatureAuthorityConfirmed: boolean) => void;
  onDownloadTerms: () => void;
};

export function SapAiTermsDialog({ open, onDecision, onDownloadTerms }: SapAiTermsDialogProps) {
  const [hasAuthority, setHasAuthority] = useState(false);
  const { hasReachedEnd, hasContentAbove, hasContentBelow, setScrollRoot, setStartMarker, setEndMarker } =
    useTermsScrollState();
  const canAccept = hasReachedEnd && hasAuthority;

  return (
    <StyledSapAiTermsDialog
      open={open}
      closable={false}
      showCloseIcon={false}
      isBlocking
      okButton={null}
      closeButton={null}
      data-test-id={SapAiTermsTestIds.dialog}
      footer={
        <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
          <StackItem grow='1'>
            <Button
              variant='tertiaryGray'
              onClick={() => onDecision(TermsResponseStatus.denied, hasAuthority)}
              data-test-id={SapAiTermsTestIds.denyButton}>
              Deny
            </Button>
          </StackItem>
          <Button
            variant='secondaryGray'
            onClick={() => onDecision(TermsResponseStatus.deferred, hasAuthority)}
            data-test-id={SapAiTermsTestIds.remindLaterButton}>
            Decide Later
          </Button>
          <Button
            variant='primary'
            disabled={!canAccept}
            onClick={() => onDecision(TermsResponseStatus.accepted, hasAuthority)}
            data-test-id={SapAiTermsTestIds.acceptButton}>
            I Accept
          </Button>
        </Stack>
      }>
      <Stack direction='vertical' gap={theme.spaces.x20}>
        <Stack direction='vertical' gap={theme.spaces.x16} align='start'>
          <FeaturedIcon variant='light' icon={<CopilotLogoMd24pxIcon />} />
          <Stack direction='vertical' gap={theme.spaces.x4}>
            <Text level='lg' weight={theme.fontWeight.semiBold} tone={theme.textColor.primary}>
              Enable Prophecy AI for your organization
            </Text>
            <Text level='sm' tone={theme.textColor.secondary}>
              Prophecy AI is available to your organization under the Early Availability program. Review and accept the
              SAP AI terms below to turn it on for your team.
            </Text>
          </Stack>
        </Stack>

        <Stack direction='vertical' gap={theme.spaces.x16}>
          <Stack direction='horizontal' align='space-between' alignY='center' gap={theme.spaces.x12}>
            <NoWrapText>
              <Text level='sm' tone={theme.textColor.secondary}>
                {`${sapAiTerms.title} · ${sapAiTerms.version} · Effective ${sapAiTerms.effectiveDate}`}
              </Text>
            </NoWrapText>
            <Button
              variant='link'
              icon={<DownloadBIcon type='default' />}
              iconPlacement='left'
              onClick={onDownloadTerms}
              data-test-id={SapAiTermsTestIds.downloadButton}>
              Download PDF
            </Button>
          </Stack>

          <TermsScrollFrame $showTopFade={hasContentAbove} $showBottomFade={hasContentBelow}>
            <TermsScrollArea ref={setScrollRoot} data-test-id={SapAiTermsTestIds.scrollArea}>
              <ScrollMarker ref={setStartMarker} />
              <Stack direction='vertical' gap={theme.spaces.x24}>
                <SapAiTermsBody terms={sapAiTerms} />
                {GEN_AI_FEATURES_CONSENT.map((paragraph, index) => (
                  <TermsRunsText key={index} paragraph={paragraph} />
                ))}
              </Stack>
              <EndOfTermsMarker ref={setEndMarker} data-test-id={SapAiTermsTestIds.endMarker}>
                <Badges
                  variant='pillLight'
                  tone={BaseColor.success}
                  size='m'
                  icon={<CheckCircleIcon type='default' />}
                  iconPlacement='left'>
                  You’ve reached the end of the terms
                </Badges>
              </EndOfTermsMarker>
            </TermsScrollArea>
          </TermsScrollFrame>

          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            {hasReachedEnd ? (
              <>
                <CheckCircleIcon type='default' color={theme.colors.success600} />
                <Text level='sm' tone={theme.textColor.secondary}>
                  Full terms reviewed — confirm your authority below to unlock “I Accept”.
                </Text>
              </>
            ) : (
              <>
                <ArrowDownIcon type='default' color={theme.colors.gray500} />
                <Text level='sm' tone={theme.textColor.secondary}>
                  Scroll to the end of the terms — “I Accept” unlocks once you’ve read them in full.
                </Text>
              </>
            )}
          </Stack>
        </Stack>

        <Stack direction='vertical' gap={theme.spaces.x6}>
          <Checkbox
            checked={hasAuthority}
            onChange={setHasAuthority}
            data-test-id={SapAiTermsTestIds.authorityCheckbox}>
            I confirm that I am authorized to accept these terms on behalf of my organization.
          </Checkbox>
          <RecordNote>
            <Text level='xs' tone={theme.colors.gray500}>
              {`Your name, response, and terms version (${sapAiTerms.version}) are recorded with a timestamp for compliance.`}
            </Text>
          </RecordNote>
        </Stack>
      </Stack>
    </StyledSapAiTermsDialog>
  );
}
